/**
 * MobileSettingsDialog — TODO-MDC-23.
 *
 * Pantalla siempre accesible desde el menú de usuario (Topbar) que
 * permite al operador activar/desactivar notificaciones push y mostrar
 * instrucciones para instalar la app, sin depender del wizard de
 * primera vez (que se cierra una vez y nunca vuelve).
 *
 * Este es el bug crítico que reportó el usuario:
 *   "no me deja activar las notificaciones en ninguno de los navegadores
 *    ni tampoco instalar la app"
 *
 * Causa: el `MobileWelcomeWizard` se mostraba 1 vez. Si el usuario lo
 * cerraba con el botón "Después" o la X, el flag `manaxer.mobile-onboarding=done`
 * quedaba en localStorage y ya nunca volvía a aparecer. No había otra
 * vía para activar push o instalar.
 *
 * Solución:
 *   - Esta pantalla muestra estado actual + botones grandes con feedback.
 *   - Funciona en cualquier momento (desktop y mobile).
 *   - Diagnóstico en pantalla: muestra plataforma detectada, soporte de
 *     push y motivo si no es soportado.
 *   - Si las notificaciones están "denied", explica cómo desbloquearlas
 *     desde el navegador (no se puede revertir programáticamente).
 *   - Si la app ya está instalada, muestra el estado consolidado.
 */

import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  BellOff,
  Check,
  Download,
  HelpCircle,
  Info,
  Loader2,
  MoreHorizontal,
  Plus,
  Share,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { Drawer } from '@/components/common/Drawer';
import { Button } from '@/components/common/Button';
import { detectPlatform, type PlatformInfo } from '@/lib/platformDetect';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { usePushSubscription } from '@/hooks/usePushSubscription';

interface Props {
  open: boolean;
  onClose: () => void;
  empresaId?: string | null;
}

export function MobileSettingsDialog({ open, onClose, empresaId }: Props) {
  const [platform, setPlatform] = useState<PlatformInfo | null>(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const install = useInstallPrompt();
  const push = usePushSubscription(empresaId ?? null);

  useEffect(() => {
    if (open) {
      setPlatform(detectPlatform());
      setShowInstallHelp(false);
    }
  }, [open]);

  if (!platform) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        width={560}
        kicker="Móvil"
        title="Configuración móvil"
      >
        <div className="flex h-40 items-center justify-center text-neutral-muted">
          <Loader2 className="animate-spin" />
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      kicker="Móvil · PWA"
      title="Configuración móvil"
      description="Activá notificaciones e instalá la app en tu celu/tablet. Funciona en Android (Chrome/Edge) e iOS (Safari)."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {showInstallHelp ? (
        <InstallHelp platform={platform} onBack={() => setShowInstallHelp(false)} />
      ) : (
        <>
          <NotificationsSection push={push} platform={platform} />
          <div className="my-5 h-px bg-surface-line" />
          <InstallSection
            platform={platform}
            install={install}
            onShowHelp={() => setShowInstallHelp(true)}
          />
          <div className="my-5 h-px bg-surface-line" />
          <DiagnosticoSection platform={platform} push={push} install={install} />
        </>
      )}
    </Drawer>
  );
}

// ─── Sección Notificaciones ────────────────────────────────────────────────

function NotificationsSection({
  push,
  platform,
}: {
  push: ReturnType<typeof usePushSubscription>;
  platform: PlatformInfo;
}) {
  const { state, enable, disable } = push;

  // Estados visibles:
  //   - subscribed: ya activadas en este device
  //   - permission='denied': el browser bloqueó, hay que desbloquear manual
  //   - !supported: el browser no soporta (iOS <16.4 sin instalar PWA)
  //   - default: aún no se preguntó

  let estado: 'on' | 'off' | 'denied' | 'unsupported' | 'busy';
  if (state.busy) estado = 'busy';
  else if (state.subscribed) estado = 'on';
  else if (!state.supported) estado = 'unsupported';
  else if (state.permission === 'denied') estado = 'denied';
  else estado = 'off';

  return (
    <section>
      <header className="mb-3 flex items-center gap-2">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-blue text-white">
          <Bell size={16} />
        </span>
        <div>
          <h3 className="text-sm font-bold text-neutral-ink">Notificaciones push</h3>
          <p className="text-[11px] text-neutral-muted">
            Recibí avisos de lotes, cobros y alertas — incluso con la app cerrada.
          </p>
        </div>
      </header>

      {estado === 'on' && (
        <div className="rounded-md border border-status-success/30 bg-status-success/5 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-status-success">
            <Check size={14} /> Activadas en este dispositivo
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              void disable().then((ok) => {
                if (ok) toast.success('Notificaciones desactivadas en este dispositivo.');
              });
            }}
            loading={state.busy}
            leftIcon={<BellOff size={14} />}
          >
            Desactivar
          </Button>
        </div>
      )}

      {estado === 'off' && (
        <div className="space-y-2">
          <Button
            className="w-full"
            onClick={() => {
              void enable().then((ok) => {
                if (ok) toast.success('Notificaciones activadas.');
                else if (push.state.error) toast.error(push.state.error);
              });
            }}
            loading={state.busy}
            leftIcon={<Bell size={14} />}
          >
            Activar notificaciones
          </Button>
          {state.error && (
            <p className="rounded-md bg-status-danger/5 p-2 text-[11px] text-status-danger">
              {state.error}
            </p>
          )}
        </div>
      )}

      {estado === 'denied' && (
        <div className="border-orange-30 bg-orange-10/40 rounded-md border p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-orange">
            <AlertTriangle size={14} /> Bloqueadas por el navegador
          </div>
          <p className="text-[11px] text-neutral-muted">
            Una vez bloqueadas, sólo podés desbloquearlas desde la configuración del
            navegador. {desbloqueoTip(platform)}
          </p>
        </div>
      )}

      {estado === 'unsupported' && (
        <div className="bg-surface-soft rounded-md border border-surface-line p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-ink">
            <Info size={14} /> No soportado en este navegador
          </div>
          <p className="text-[11px] text-neutral-muted">
            {state.reason === 'no_vapid_key' &&
              'La empresa todavía no configuró el sistema de notificaciones — avisá al equipo técnico.'}
            {state.reason === 'no_notification_api' &&
              'Este navegador no expone la API de notificaciones. Probá con Chrome, Edge o Safari.'}
            {state.reason === 'no_service_worker' &&
              'Falta el Service Worker — abrí la app en https:// (no en http) y recargá.'}
            {state.reason === 'no_push_manager' &&
              'iOS soporta push solo cuando la app está INSTALADA (Compartir → Agregar a pantalla de inicio). Instalá la app primero y volvé a entrar.'}
            {!state.reason &&
              'No se detecta soporte. Probá con Chrome, Edge (Android) o Safari iOS 16.4+ con la app instalada.'}
          </p>
        </div>
      )}

      {estado === 'busy' && (
        <div className="rounded-md border border-surface-line bg-white p-3 text-sm text-neutral-muted">
          <Loader2 size={14} className="mr-2 inline animate-spin" /> Procesando…
        </div>
      )}
    </section>
  );
}

function desbloqueoTip(platform: PlatformInfo): string {
  if (platform.isIOS && platform.isSafari) {
    return 'En iOS: Ajustes → Notificaciones → MANAXER → activar permiso (solo si la app está instalada).';
  }
  if (platform.isIOS) {
    return 'En iOS sólo Safari (con la app instalada) maneja notificaciones push.';
  }
  if (platform.isAndroid) {
    return 'En Chrome Android: tocá el candado en la barra de URL → Permisos → Notificaciones → Permitir.';
  }
  return 'En desktop: candado en la barra de URL → Configuración del sitio → Notificaciones → Permitir.';
}

// ─── Sección Instalar ─────────────────────────────────────────────────────

function InstallSection({
  platform,
  install,
  onShowHelp,
}: {
  platform: PlatformInfo;
  install: ReturnType<typeof useInstallPrompt>;
  onShowHelp: () => void;
}) {
  if (platform.isStandalone) {
    return (
      <section>
        <header className="mb-3 flex items-center gap-2">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-status-success/15 text-status-success">
            <Check size={16} />
          </span>
          <div>
            <h3 className="text-sm font-bold text-neutral-ink">App instalada</h3>
            <p className="text-[11px] text-neutral-muted">
              Estás usando MANAXER como app — todo OK.
            </p>
          </div>
        </header>
      </section>
    );
  }

  return (
    <section>
      <header className="mb-3 flex items-center gap-2">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-orange text-white">
          <Smartphone size={16} />
        </span>
        <div>
          <h3 className="text-sm font-bold text-neutral-ink">Instalar como app</h3>
          <p className="text-[11px] text-neutral-muted">
            Acceso directo en el escritorio del celu, abre en pantalla completa.
          </p>
        </div>
      </header>

      <div className="space-y-2">
        {install.available ? (
          <Button
            className="w-full"
            variant="accent"
            leftIcon={<Download size={14} />}
            onClick={() => {
              void install.promptInstall().then((accepted) => {
                if (accepted)
                  toast.success('App instalada — buscala en tu pantalla de inicio.');
              });
            }}
          >
            Instalar ahora (1-click)
          </Button>
        ) : (
          <>
            <Button
              className="w-full"
              variant="secondary"
              leftIcon={<HelpCircle size={14} />}
              onClick={onShowHelp}
            >
              Ver instrucciones para mi navegador
            </Button>
            <p className="text-[11px] text-neutral-muted">
              {platform.isIOS &&
                platform.isSafari &&
                'iOS Safari requiere instalar manualmente desde el menú Compartir.'}
              {platform.isIOS &&
                !platform.isSafari &&
                'En iPhone solo Safari permite instalar. Abrí MANAXER en Safari.'}
              {platform.isAndroid &&
                !platform.isChrome &&
                !platform.isEdge &&
                'Recomendamos Chrome o Edge en Android — ofrecen instalación directa.'}
              {!platform.isIOS &&
                !platform.isAndroid &&
                'En desktop podés instalar desde el icono de la barra de direcciones del navegador.'}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function InstallHelp({
  platform,
  onBack,
}: {
  platform: PlatformInfo;
  onBack: () => void;
}) {
  let title = 'Cómo instalar MANAXER';
  let steps: Array<{ icon: React.ReactNode; text: React.ReactNode }> = [];

  if (platform.isIOS && platform.isSafari) {
    title = 'Instalar en iPhone / iPad (Safari)';
    steps = [
      {
        icon: <Share size={16} className="text-blue" />,
        text: (
          <>
            Tocá <b>Compartir</b> en la barra inferior (cuadrado con flecha hacia arriba).
          </>
        ),
      },
      {
        icon: <Plus size={16} className="text-blue" />,
        text: (
          <>
            En el menú, deslizá hasta <b>"Agregar a pantalla de inicio"</b> (ícono +).
          </>
        ),
      },
      {
        icon: <Bell size={16} className="text-blue" />,
        text: (
          <>
            Confirmá <b>Agregar</b> arriba a la derecha. El ícono de MANAXER aparece en tu
            escritorio.
          </>
        ),
      },
    ];
  } else if (platform.isIOS) {
    title = 'En iPhone usá Safari';
    steps = [
      {
        icon: <Share size={16} className="text-orange" />,
        text: (
          <>
            En iPhone sólo Safari permite agregar apps a la pantalla de inicio. Abrí
            MANAXER en Safari y volvé a Configuración móvil.
          </>
        ),
      },
    ];
  } else if (platform.isAndroid) {
    title = 'Instalar en Android';
    steps = [
      {
        icon: <MoreHorizontal size={16} className="text-blue" />,
        text: (
          <>
            Tocá el botón de <b>menú</b> (3 puntos verticales) arriba a la derecha del
            navegador.
          </>
        ),
      },
      {
        icon: <Download size={16} className="text-blue" />,
        text: (
          <>
            Tocá <b>"Instalar app"</b> o <b>"Agregar a pantalla principal"</b>.
          </>
        ),
      },
      {
        icon: <Bell size={16} className="text-blue" />,
        text: <>Confirmá. El ícono aparece en tu pantalla de inicio.</>,
      },
    ];
  } else {
    title = 'Instalar en desktop';
    steps = [
      {
        icon: <Download size={16} className="text-blue" />,
        text: (
          <>
            En la barra de direcciones buscá un ícono de <b>Instalar</b> (a la derecha de
            la URL en Chrome/Edge). Click → Instalar.
          </>
        ),
      },
    ];
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-blue hover:underline"
      >
        ← Volver
      </button>
      <h2 className="text-lg font-extrabold tracking-tight text-neutral-ink">{title}</h2>
      <p className="mt-1 text-xs text-neutral-muted">
        Una vez instalada, la abrís desde el ícono. En iOS instalada también recibís
        notificaciones push.
      </p>

      <ol className="mt-4 space-y-3">
        {steps.map((s, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-xl border border-surface-line bg-white p-3"
          >
            <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-blue-10 font-bold text-blue">
              {i + 1}
            </span>
            <div className="flex-1 text-sm text-neutral-ink">{s.text}</div>
            <span className="flex-none">{s.icon}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─── Sección Diagnóstico (sutil, abajo) ────────────────────────────────────

function DiagnosticoSection({
  platform,
  push,
  install,
}: {
  platform: PlatformInfo;
  push: ReturnType<typeof usePushSubscription>;
  install: ReturnType<typeof useInstallPrompt>;
}) {
  const browser = platform.isChrome
    ? 'Chrome'
    : platform.isEdge
      ? 'Edge'
      : platform.isSafari
        ? 'Safari'
        : platform.isFirefox
          ? 'Firefox'
          : 'Otro';
  const so = platform.isIOS ? 'iOS' : platform.isAndroid ? 'Android' : 'Desktop';
  return (
    <section className="bg-surface-soft rounded-md border border-surface-line p-3">
      <p className="kicker mb-2 flex items-center gap-1.5">
        <Info size={11} /> Diagnóstico
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
        <dt className="text-neutral-muted">Plataforma</dt>
        <dd className="text-neutral-ink">
          {so} · {browser}
        </dd>
        <dt className="text-neutral-muted">App instalada</dt>
        <dd className="text-neutral-ink">{platform.isStandalone ? 'Sí' : 'No'}</dd>
        <dt className="text-neutral-muted">Push soportado</dt>
        <dd className="text-neutral-ink">
          {push.state.supported ? 'Sí' : `No (${push.state.reason ?? 'desconocido'})`}
        </dd>
        <dt className="text-neutral-muted">Permiso push</dt>
        <dd className="text-neutral-ink">{push.state.permission}</dd>
        <dt className="text-neutral-muted">Suscripto</dt>
        <dd className="text-neutral-ink">{push.state.subscribed ? 'Sí' : 'No'}</dd>
        <dt className="text-neutral-muted">Install prompt</dt>
        <dd className="text-neutral-ink">
          {install.available ? 'Disponible' : 'No disponible'}
        </dd>
      </dl>
    </section>
  );
}
